<script setup>
import { useI18n } from 'vue-i18n';
import whatsappIcon from 'dashboard/assets/images/whatsapp.png';

defineProps({
  approvedCount: {
    type: Number,
    default: 0,
  },
  inboxCount: {
    type: Number,
    default: 0,
  },
  isFetching: {
    type: Boolean,
    default: false,
  },
  isSyncingTemplates: {
    type: Boolean,
    default: false,
  },
  isSyncDisabled: {
    type: Boolean,
    default: false,
  },
  hasInboxes: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['syncTemplates', 'openBuilder']);

const { t } = useI18n();
</script>

<template>
  <!-- eslint-disable vue/no-static-inline-styles -->
  <header class="relative overflow-hidden">
    <div class="bg-green-900/95">
      <div
        class="mx-auto px-4 py-4 flex items-center justify-between flex-wrap gap-4"
      >
        <!-- Left: icon + title -->
        <div class="flex items-center gap-4">
          <div>
            <img
              :src="whatsappIcon"
              alt="WhatsApp Business"
              class="w-10 h-10 object-contain drop-shadow-sm"
            />
          </div>
          <div class="flex flex-col gap-1">
            <h1 class="text-3xl font-bold text-white m-0">
              {{ t('CAMPAIGN.WHATSAPP_OFFICIAL.HEADER_TITLE') }}
            </h1>
            <p class="text-green-100 dark:text-green-200 max-w-2xl m-0 text-lg">
              {{ t('CAMPAIGN.WHATSAPP_OFFICIAL.GUIDELINE_TEXT') }}
            </p>
          </div>
        </div>

        <!-- Right: stats -->
        <div class="flex gap-4 flex-wrap items-center">
          <!-- Protótipo do builder de campanha em nodes -->
          <button
            type="button"
            class="flex items-center gap-2 rounded-xl border border-white/20 bg-white/10 px-4 py-2 text-sm font-medium text-white transition-all duration-200 hover:bg-white/20"
            @click="emit('openBuilder')"
          >
            <span class="i-lucide-workflow w-4 h-4" />
            {{ t('CAMPAIGN.WHATSAPP_OFFICIAL.BUILDER.ENTRY_BUTTON') }}
          </button>
          <!-- Skeleton stats -->
          <template v-if="isFetching">
            <div
              v-for="i in 2"
              :key="i"
              class="bg-green-800/50 rounded-xl px-6 py-4 text-center w-24 h-20"
            >
              <div
                class="h-6 bg-gradient-to-r from-green-600 via-green-500 to-green-600 rounded mb-2"
                style="
                  background-size: 200% 100%;
                  animation: shimmer 2s infinite;
                "
              />
              <div
                class="h-3 bg-gradient-to-r from-green-600 via-green-500 to-green-600 rounded"
                style="
                  background-size: 200% 100%;
                  animation: shimmer 2s infinite;
                "
              />
            </div>
          </template>

          <!-- Real stats -->
          <template v-else>
            <!-- Approved templates card with sync button -->
            <div
              class="bg-green-900 dark:bg-green-800 backdrop-blur-sm rounded-xl px-6 py-4 text-center relative"
            >
              <div class="text-2xl font-bold text-white">
                {{ approvedCount }}
              </div>
              <div class="text-sm text-green-100 dark:text-green-200">
                {{ t('CAMPAIGN.WHATSAPP_OFFICIAL.STATS.APPROVED_TEMPLATES') }}
              </div>
              <div v-if="hasInboxes" class="absolute top-2 right-2">
                <button
                  v-tooltip.right="
                    isSyncDisabled
                      ? t('CAMPAIGN.WHATSAPP_OFFICIAL.SYNC.TOOLTIP_DISABLED')
                      : t('CAMPAIGN.WHATSAPP_OFFICIAL.SYNC.TOOLTIP_ENABLED')
                  "
                  :disabled="isSyncingTemplates || isSyncDisabled"
                  class="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-all duration-200 border-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:not-disabled:scale-105"
                  :class="{ 'bg-white/30': isSyncingTemplates }"
                  @click="emit('syncTemplates')"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="w-4 h-4"
                    :class="{ 'animate-spin': isSyncingTemplates }"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                </button>
              </div>
            </div>

            <!-- WhatsApp accounts card -->
            <div
              class="bg-green-900 dark:bg-green-800 backdrop-blur-sm rounded-xl px-6 py-4 text-center"
            >
              <div class="text-2xl font-bold text-white">{{ inboxCount }}</div>
              <div class="text-sm text-green-100 dark:text-green-200">
                {{ t('CAMPAIGN.WHATSAPP_OFFICIAL.STATS.WHATSAPP_ACCOUNTS') }}
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </header>
</template>

<style scoped>
@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}
</style>
